import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001/api';

const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// ── Request interceptor — attach JWT token to every request ──────────────────
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('ilakku_token');
    if (token) {
      config.headers['Authorization'] = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// ── Response interceptor — auto-logout on 401 (expired/invalid token) ────────
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Clear stored credentials and redirect to login
      localStorage.removeItem('ilakku_token');
      localStorage.removeItem('ilakku_user');
      // Reload the page — App.js will detect no user and show Login
      window.location.href = '/';
    }
    return Promise.reject(error);
  }
);

// Employees API
export const employeesAPI = {
  getAll: () => api.get('/employees'),
  getById: (id) => api.get(`/employees/${id}`),
  getOverallocated: () => api.get('/employees/overallocated'),
  checkAllocation: (id, params) => api.get(`/employees/${id}/allocation-check`, { params }),
  getResourceView: (start, end) => api.get('/employees/resource-view', { params: { start, end } }),
  getResourceSummary: () => api.get('/employees/resource-summary'),
  getWeeklyView: () => api.get('/employees/weekly-view'),
  getSchedule: (id, from, to) => api.get(`/employees/${id}/schedule`, { params: { from, to } }),
  saveSchedule: (id, data) => api.post(`/employees/${id}/schedule`, data),
  create: (data) => api.post('/employees', data),
  update: (id, data) => api.put(`/employees/${id}`, data),
  delete: (id) => api.delete(`/employees/${id}`),
};

// Projects API
export const projectsAPI = {
  getAll: () => api.get('/projects'),
  getById: (id) => api.get(`/projects/${id}`),
  getStats: (id) => api.get(`/projects/${id}/stats`),
  getChangeLogs: (id) => api.get(`/projects/${id}/change-logs`),
  getTaskChangeLogs: (id) => api.get(`/projects/${id}/task-change-logs`),
  getNotes: (id) => api.get(`/projects/${id}/notes`),
  createNote: (id, data) => api.post(`/projects/${id}/notes`, data),
  deleteNote: (noteId) => api.delete(`/projects/notes/${noteId}`),
  create: (data) => api.post('/projects', data),
  update: (id, data) => api.put(`/projects/${id}`, data),
  delete: (id) => api.delete(`/projects/${id}`),
};

// Tasks API
export const tasksAPI = {
  getAllByProject: (projectId) => api.get(`/projects/${projectId}/tasks`),
  getById: (id) => api.get(`/tasks/${id}`),
  getByEmployee: (employeeId) => api.get(`/tasks/by-employee/${employeeId}`),
  getQueue: (employeeId) => api.get(`/tasks/queue/${employeeId}`),
  reorderQueue: (employeeId, data) => api.put(`/tasks/queue/${employeeId}/reorder`, data),
  toggleHold: (id, on_hold, effective_date, changed_by_id, changed_by_name) => api.put(`/tasks/${id}/hold`, { on_hold, effective_date, changed_by_id, changed_by_name }),
  setUnplanned: (id, is_unplanned, unplanned_reason, changed_by_id, changed_by_name) => api.put(`/tasks/${id}/unplanned`, { is_unplanned, unplanned_reason, changed_by_id, changed_by_name }),
  getOverdue: () => api.get('/tasks/overdue'),
  getUnplanned: () => api.get('/tasks/unplanned'),
  getByDateRange: (start, end) => api.get('/tasks/due-in-range', { params: { start, end } }),
  getSubtasks: (id) => api.get(`/tasks/${id}/subtasks`),
  getChangeLogs: (id) => api.get(`/tasks/${id}/change-logs`),
  create: (projectId, data) => api.post(`/projects/${projectId}/tasks`, data),
  update: (id, data) => api.put(`/tasks/${id}`, data),
  delete: (id, data) => api.delete(`/tasks/${id}`, { data }),
};

// Change Requests API
export const changeRequestsAPI = {
  getAll: (params) => api.get('/change-requests', { params }),
  getById: (id) => api.get(`/change-requests/${id}`),
  create: (data) => api.post('/change-requests', data),
  update: (id, data) => api.put(`/change-requests/${id}`, data),
  delete: (id) => api.delete(`/change-requests/${id}`),
  getLinkedTasks: (id) => api.get(`/change-requests/${id}/tasks`),
  setLinkedTasks: (id, taskIds) => api.put(`/change-requests/${id}/tasks`, { task_ids: taskIds }),
};

// RAID API
export const raidAPI = {
  // Risks
  getRisks:         (params)    => api.get('/raid/risks', { params }),
  createRisk:       (data)      => api.post('/raid/risks', data),
  updateRisk:       (id, data)  => api.put(`/raid/risks/${id}`, data),
  deleteRisk:       (id)        => api.delete(`/raid/risks/${id}`),
  // Assumptions
  getAssumptions:   (params)    => api.get('/raid/assumptions', { params }),
  createAssumption: (data)      => api.post('/raid/assumptions', data),
  updateAssumption: (id, data)  => api.put(`/raid/assumptions/${id}`, data),
  deleteAssumption: (id)        => api.delete(`/raid/assumptions/${id}`),
  // Issues
  getIssues:        (params)    => api.get('/raid/issues', { params }),
  createIssue:      (data)      => api.post('/raid/issues', data),
  updateIssue:      (id, data)  => api.put(`/raid/issues/${id}`, data),
  deleteIssue:      (id)        => api.delete(`/raid/issues/${id}`),
  // Dependencies
  getDependencies:  (params)    => api.get('/raid/dependencies', { params }),
  createDependency: (data)      => api.post('/raid/dependencies', data),
  updateDependency: (id, data)  => api.put(`/raid/dependencies/${id}`, data),
  deleteDependency: (id)        => api.delete(`/raid/dependencies/${id}`),
};

// Project Plans API
export const plansAPI = {
  getAll:           ()            => api.get('/plans'),
  getById:          (id)          => api.get(`/plans/${id}`),
  create:           (data)        => api.post('/plans', data),
  update:           (id, data)    => api.put(`/plans/${id}`, data),
  delete:           (id)          => api.delete(`/plans/${id}`),
  addTask:          (id, data)    => api.post(`/plans/${id}/tasks`, data),
  updateTask:       (id, taskId, data) => api.put(`/plans/${id}/tasks/${taskId}`, data),
  deleteTask:       (id, taskId)  => api.delete(`/plans/${id}/tasks/${taskId}`),
  addDependency:    (id, data)    => api.post(`/plans/${id}/dependencies`, data),
  deleteDependency: (id, depId)   => api.delete(`/plans/${id}/dependencies/${depId}`),
  recompute:        (id)          => api.post(`/plans/${id}/recompute`),
  createProject:    (id)          => api.post(`/plans/${id}/create-project`),
};

export default api; // Rule 14: export default LAST
